"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, ClipboardCheck, ShieldCheck, ArrowRight, Clock, CircleCheck as CheckCircle2, Phone } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
export default function Process() {
  const shouldReduceMotion = useReducedMotion();
  const steps = [{
    step: "01",
    title: "Tell Us What's Happening",
    description: "Call or submit a service request to speak directly with our 24/7 dispatch team and describe your plumbing issue.",
    icon: PhoneCall,
    highlight: "Call or Submit Request"
  }, {
    step: "02",
    title: "Diagnose the Problem",
    description: "The plumbing issue is evaluated thoroughly on-site to inspect the root cause and determine the right solution.",
    icon: ClipboardCheck,
    highlight: "Accurate Evaluation"
  }, {
    step: "03",
    title: "Get the Appropriate Service",
    description: "The appropriate repair or service is recommended and completed cleanly with clear upfront pricing and guaranteed quality.",
    icon: ShieldCheck,
    highlight: "Recommended Solution"
  }];
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="how-it-works" data-nav="light" className="relative bg-background py-14 md:py-20 lg:py-24">
      <div className="mx-auto max-w-[1240px] px-5 md:px-10">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary" className="mb-3 px-3 py-1 text-xs font-bold uppercase tracking-wider text-primary">
            Simple 3-Step Process
          </Badge>
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            How Our Plumbing Service Works
          </h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            When water is flooding your property or drains are backing up, every minute counts. We deliver fast, transparent plumbing service in Jonesboro from first call to final repair.
          </p>
        </div>

        {/* Steps Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {steps.map((item, index) => {
          const Icon = item.icon;
          return <motion.div key={item.step} variants={itemVariants} className="flex">
                <Card data-index={index} className="group relative flex w-full flex-col justify-between overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
                  {/* Decorative Step Top Bar Accent */}
                  <div className="absolute top-0 left-0 h-1.5 w-full bg-muted transition-colors duration-200 group-hover:bg-primary" />

                  <div>
                    <CardHeader className="p-0">
                      <div className="flex items-center justify-between">
                        <div className="flex size-12 items-center justify-center rounded-xl bg-secondary text-primary">
                          <Icon className="size-6" />
                        </div>
                        <span className="font-mono text-2xl font-extrabold tracking-tight text-muted-foreground/60 group-hover:text-primary">
                          {item.step}
                        </span>
                      </div>
                      <CardTitle className="mt-5 text-lg font-bold text-card-foreground">
                        {item.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="mt-3 p-0">
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        {item.description}
                      </p>
                    </CardContent>
                  </div>

                  {/* Step Micro-badge / Verification */}
                  <div className="mt-6 border-t border-border/80 pt-4">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-primary">
                      <CheckCircle2 className="size-3.5 text-accent" />
                      <span>{item.highlight}</span>
                    </div>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Action Anchor Banner */}
        <div className="mt-12 rounded-2xl border border-border bg-secondary/50 p-6 sm:p-8 md:p-10">
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <div className="text-center md:text-left">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <Clock className="size-4 text-accent" />
                <span>Available 24 Hours A Day, 7 Days A Week</span>
              </div>
              <h3 className="mt-1 text-xl font-bold text-foreground sm:text-2xl">
                Need urgent plumbing service in Jonesboro right now?
              </h3>
              <p className="mt-1 text-sm text-muted-foreground sm:text-base">
                Call our direct dispatch line or submit a service request online.
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button asChild size="lg" className="rounded-full bg-accent px-6 py-6 text-base font-bold text-accent-foreground shadow-sm hover:bg-accent/90 focus-visible:ring-ring">
                <a href="tel:4708479076" className="flex items-center gap-2">
                  <Phone className="size-5 fill-current" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-full border-border bg-background px-6 py-6 text-base font-semibold text-foreground hover:bg-muted">
                <Link to="/contact/" className="flex items-center gap-2">
                  <span>Request Service</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}